import { Range } from '@attain-apps/range'
import { TypeAsserter } from '@attain-apps/type-guards'

import type * as admin from '../../admin'
import type * as common from '../../common'

import type { Activity } from './activity'
import { Execution } from './execution'
import type { Item } from './item'
import * as templates from './templates'

type ProcessBase = common.Identified<Process.Id> &
  Omit<Rename.Property<templates.Template, 'id', 'templateId'>, 'getSteps'> &
  Process.Context
/**
 * Represents some task that could take a non-trivial amount of time for which
 * the user may want updates on how the task is progressing
 *
 * For example, a file import process may take several minutes, and the user may
 * want to see as each part of the import succeeds, and if it fails, they will
 * want to know which part failed
 *
 * Usually, this can be produced from {@link Process.Essentials} using
 * {@link Process.from}
 */
export interface Process extends ProcessBase {
  /**
   * @see {@link Process.Step}
   */
  readonly steps: readonly Process.Step[]
}
export namespace Process {
  /**
   * Identifies a {@link Process} of a certain {@link templates.Template.Id}
   * within a {@link Process.Context}
   */

  export type Id = string

  /**
   *  Type used for UI purpose, to get all properties needed for displaying
   * elements of process
   */
  export interface WithDetails extends Process {
    /** Indicates end date of Process, if exists, otherwise start date */
    readonly date: Date
    /** Indicates the current status of Process */
    readonly status: Execution.Status
    /**
     * Number between 0 and 1 indicating the overall completion of the Process,
     * calculated in @see {@link calculateCompletionPercent}
     */
    readonly progress: number
    /**
     * Extra details about the Process,
     *
     * e.g., what went wrong.
     */
    readonly detail?: string
    /**
     * Duration in milliseconds of how long the process ran for.
     * Only defined when the process has both start and end times.
     */
    readonly duration?: number
    /**
     * Indicates that the process completed with some steps failing and some
     * succeeding. This is a display-only property computed from the step statuses.
     */
    readonly isPartiallyFailed: boolean
  }
  export namespace WithDetails {
    /**
     * Constructs a full {@link Process.WithDetails} from its {@link Essentials}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function from(essentials: Readonly.Deep<Essentials>): WithDetails {
      const processFromEssentials = Process.from(essentials)
      const status = TypeAsserter.validate(
        essentials.status,
        TypeAsserter.assertIsNotNullish,
      )
      return {
        ...processFromEssentials,
        date: TypeAsserter.validate(
          essentials.timeframe.end ?? essentials.timeframe.start,
          TypeAsserter.assertIsNotNullish,
        ),
        detail: essentials.detail,
        duration:
          essentials.timeframe.start && essentials.timeframe.end
            ? essentials.timeframe.end.getTime() -
              essentials.timeframe.start.getTime()
            : undefined,
        isPartiallyFailed: Process.isPartiallyFailed(processFromEssentials),
        progress: Process.calculateCompletionPercent(processFromEssentials),
        status,
      }
    }
  }

  /**
   * Refers to a specific {@link Process} within a {@link Process.Context}
   */
  export type Ref = Readonly<
    Rename.Property<Pick<Process, 'id' | 'templateId'>, 'id', 'processId'>
  >

  export namespace Ref {
    /**
     * Fully uniquely identifies a {@link Process}
     */
    export type InContext = Ref & Context
  }

  /**
   * An {@link admin.Context} in which a {@link Process} can occur
   */
  export type Context = AllowMissing<
    // NOTE: FlattenUnion makes this easier to consume as it allows consumers to
    // reference `context.appId` and `context.scenarioId`, while AllowMissing
    // makes this easier to produce, as it means the producer does not have to
    // include `appId` or `scenarioId`
    FlattenUnion<
      admin.Context.ForZone | admin.Context.ForApp | admin.Context.ForScenario
    >
  >

  type StepBase = Readonly<
    common.Named & common.PotentiallyDescribed & Execution
  >
  /**
   * An atomic part of a {@link Process} from an end user's perspective. In most
   * cases, the step is not actually the lowest level of progress that can be
   * reported, but it is the lowest level that is relevant for an end user
   *
   * For example, when importing a file, the steps might be:
   * 1. Receive the file
   * 1. Parse its contents
   * 1. Validate data
   * 1. Write data into the system
   */

  export type Step = StepBase

  type EssentialsBase = Pick<
    Process,
    Key.In.Union<Context> | 'id' | 'templateId'
  >
  /**
   * Conveys everything about an {@link Process} that does not come from its
   * associated {@link Template}
   */
  export interface Essentials extends EssentialsBase, Execution {
    /**
     * Used, alongside {@link items}, to derive the {@link Process.steps}
     */
    activityExecutions: Partial<
      Record<Activity.Execution.Id, Activity.Execution>
    >
    /**
     * Used, alongside {@link activityExecutions}, to derive the
     * {@link Process.steps}
     */
    items: readonly Item[]
  }

  /**
   * Constructs a full {@link Process} from its {@link Essentials}
   *
   * When the {@link templates.Template} for the given `templateId` no longer
   * exists (e.g., historical records referencing a removed template), a
   * degraded {@link Process} is returned with the raw `templateId` as its
   * name and an empty set of steps.
   */
  export function from(essentials: Readonly.Deep<Essentials>): Process {
    const template = templates.get(essentials.templateId)
    return {
      appId: essentials.appId,
      description: template?.description,
      id: essentials.id,
      name: template?.name ?? essentials.templateId,
      scenarioId: essentials.scenarioId,
      steps: template?.getSteps(essentials) ?? [],
      templateId: essentials.templateId,
      tenantId: essentials.tenantId,
      zoneId: essentials.zoneId,
    } satisfies NoMissing<Process>
  }

  /**
   * List of {@link Execution.Status} ordered such that if multiple statuses
   * appear in {@link Process.steps}, the first in this list is the most
   * relevant one to use when summarizing the {@link Process} through
   * {@link getOverallExecution}
   */
  const STATUSES_ORDERED_BY_IMPORTANCE = [
    // If a step has failed, the process has failed
    Execution.Status.Failed,
    // If nothing has failed, but some of the steps are stopping prematurely,
    // then the process is stopping
    Execution.Status.Stopping,
    // If none of the steps have failed or are stopping prematurely, and at
    // least one step is in progress, then the process is in progress
    Execution.Status.InProgress,
    // If all of the steps have not started, the process has not started.
    // However, if only some of the steps have not started, and the rest have
    // succeeded, then the process should actually be considered in progress.
    // That edge case is handled in the function below
    undefined,
    // Only when all of the steps have succeeded, can we say the process has
    // succeeded
    Execution.Status.Succeeded,
  ]
  /**
   * Produces an {@link Execution} that best summarizes the {@link Process} by
   * aggregating across {@link Process.steps}
   */
  export function getOverallExecution(
    process: Readonly.Deep<Process>,
  ): Execution {
    const execution: Mutable<Execution> = {
      timeframe: Range.empty(),
    }
    /** Lower is more relevant, 0 = most relevant */
    let currentStatusRelevance = Infinity

    for (const step of process.steps) {
      // The process's execution timeframe is from the start of the first step
      // to the end of the last step (or no end if at least one step has not
      // ended yet). This is equivalent to unioning together the timeframes of
      // each step
      execution.timeframe = execution.timeframe.union(step.timeframe)

      // For status and detail, we want to consider the most relevant status
      // (e.g. if some steps have succeeded, one has failed, and the rest have
      // not started; the one that failed is the most important one to surface)
      const stepStatusRelevance = STATUSES_ORDERED_BY_IMPORTANCE.indexOf(
        step.status,
      )
      // NOTE: < means the first step of this relevance is used, while <= would
      // mean the last step of this relevance is used (e.g. if two steps have
      // failed, < means use the first failed step, while <= means used the
      // second failed step). We prefer using the first because sometimes issues
      // in earlier steps propagate to later steps, so surfacing the issue from
      // the earliest step is usually the most helpful approach
      if (stepStatusRelevance < currentStatusRelevance) {
        execution.status = step.status
        execution.detail = step.detail
        currentStatusRelevance = stepStatusRelevance
      }
    }
    if (execution.status === undefined && !execution.timeframe.isEmpty) {
      // execution.status should only be `undefined` if all steps have either
      // succeeded or not started (i.e. status = undefined). If
      // timeframe.isEmpty is not true, then at least one step has executed,
      // which means the overall process is in progress
      execution.status = Execution.Status.InProgress
    }
    return execution
  }

  /**
   * Returns a number between `0` and `1` indicating the overall completion of
   * the {@link Process}
   *
   * Completion is defined as steps that have finished executing (either
   * succeeded or failed), not just successful steps.
   */
  export function calculateCompletionPercent(
    execution: Readonly.Deep<Process>,
  ): number {
    if (execution.steps.length === 0) {
      return 1
    }
    return (
      // # completed steps (succeeded + failed) / # steps
      execution.steps.reduce(
        (numCompleted, step) =>
          numCompleted +
          (step.status === Execution.Status.Succeeded ||
          step.status === Execution.Status.Failed
            ? 1
            : 0),
        0,
      ) / execution.steps.length
    )
  }

  /**
   * Returns true if the process has failed overall but at least one step
   * succeeded. This indicates a partial failure where some work completed
   * successfully.
   *
   * Note: This computes directly from step statuses rather than relying on
   * the overall process status, as the backend may report different statuses.
   */
  export function isPartiallyFailed(process: Readonly.Deep<Process>): boolean {
    const hasSucceededStep = process.steps.some(
      step => step.status === Execution.Status.Succeeded,
    )
    const hasFailedStep = process.steps.some(
      step => step.status === Execution.Status.Failed,
    )
    return hasSucceededStep && hasFailedStep
  }
}
